import { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { getPeriodoUltimosNDias, type PeriodoFiltro } from '@/lib/utils/formatters';

interface PeriodoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onAplicar: (filtro: PeriodoFiltro) => void;
  initialFiltro?: Partial<PeriodoFiltro>;
}

export function PeriodoModal({ open, onOpenChange, onAplicar, initialFiltro }: PeriodoModalProps) {
  const meses = [
    'Janeiro',
    'Fevereiro',
    'Março',
    'Abril',
    'Maio',
    'Junho',
    'Julho',
    'Agosto',
    'Setembro',
    'Outubro',
    'Novembro',
    'Dezembro',
  ];

  const opcoesReferente = [
    { value: 'emissao', label: 'Data de Emissão', disabled: false },
    { value: 'armazenamento', label: 'Data de Armazenamento', disabled: true },
    { value: 'entrada', label: 'Data de Entrada', disabled: true },
    { value: 'conversao', label: 'Data de Conversão', disabled: true },
  ];
  const periodoPadrao = getPeriodoUltimosNDias(30);
  const [modo, setModo] = useState(initialFiltro?.modo || 'periodo');
  const [mes, setMes] = useState(
    initialFiltro?.mes ||
      [
        'Janeiro',
        'Fevereiro',
        'Março',
        'Abril',
        'Maio',
        'Junho',
        'Julho',
        'Agosto',
        'Setembro',
        'Outubro',
        'Novembro',
        'Dezembro',
      ][new Date().getMonth()]
  );
  const [ano, setAno] = useState(initialFiltro?.ano || new Date().getFullYear());
  const [dataInicio, setDataInicio] = useState(
    initialFiltro?.dataInicio || periodoPadrao.dataInicio
  );
  const [dataFim, setDataFim] = useState(initialFiltro?.dataFim || periodoPadrao.dataFim);
  const [referente, setReferente] = useState(initialFiltro?.referente || 'emissao');

  useEffect(() => {
    if (!open) return;
    const next: Partial<PeriodoFiltro> = initialFiltro ?? {};
    const fallback = getPeriodoUltimosNDias(30);
    setModo(next.modo || 'periodo');
    setMes(next.mes || 'Dezembro');
    setAno(next.ano || new Date().getFullYear());
    setDataInicio(next.dataInicio || fallback.dataInicio);
    setDataFim(next.dataFim || fallback.dataFim);
    setReferente(next.referente || 'emissao');
  }, [open, initialFiltro]);

  const handleAplicar = () => {
    onAplicar({ modo, mes, ano, dataInicio, dataFim, referente });
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md p-6">
        <DialogHeader>
          <DialogTitle>Definir Período</DialogTitle>
          <DialogDescription>
            Escolha o período para filtrar os documentos fiscais:
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-6 mt-2">
          <div>
            <label className="flex items-center gap-2">
              <input type="radio" checked={modo === 'mesano'} onChange={() => setModo('mesano')} />
              Mês/Ano:
            </label>
            {modo === 'mesano' && (
              <div className="flex gap-2 mt-2">
                <Select value={mes} onValueChange={setMes}>
                  <SelectTrigger className="w-32">
                    <SelectValue placeholder="Mês" />
                  </SelectTrigger>
                  <SelectContent>
                    {meses.map(m => (
                      <SelectItem key={m} value={m}>
                        {m}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  className="w-20"
                  value={ano}
                  min={2000}
                  max={2100}
                  onChange={e => setAno(Number(e.target.value))}
                />
              </div>
            )}
          </div>
          <div>
            <label className="flex items-center gap-2">
              <input
                type="radio"
                checked={modo === 'periodo'}
                onChange={() => setModo('periodo')}
              />
              Em um período determinado:
            </label>
            {modo === 'periodo' && (
              <div className="flex gap-2 mt-2">
                <Input
                  type="date"
                  value={dataInicio}
                  onChange={e => setDataInicio(e.target.value)}
                />
                <span>até</span>
                <Input type="date" value={dataFim} onChange={e => setDataFim(e.target.value)} />
              </div>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <Label>Referente a:</Label>
            <Select value={referente} onValueChange={setReferente}>
              <SelectTrigger className="w-full">
                <SelectValue placeholder="Referente a" />
              </SelectTrigger>
              <SelectContent>
                {opcoesReferente.map(op => (
                  <SelectItem key={op.value} value={op.value} disabled={op.disabled}>
                    {op.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Fechar
            </Button>
            <Button onClick={handleAplicar}>Aplicar</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
